import {
  ArrowDownSmallIcon,
  SearchIcon,
  ViewLayersIcon,
} from '@blocksuite/icons/rc';
import { SlidersVertical } from 'lucide-react';
import { Button, Divider, Menu, Scrollable } from '@pique/component';
import { useAsyncCallback } from '@pique/core/hooks/pique-async-hooks';
import { useNavigateHelper } from '@pique/core/hooks/use-navigate-helper';
import type { Tag } from '@pique/core/modules/tag';
import { TagService } from '@pique/core/modules/tag';
import { isNewTabTrigger } from '@pique/core/utils';
import type { Collection } from '@pique/env/filter';
import { useI18n } from '@pique/i18n';
import {
  useLiveData,
  useService,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import type { MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Step } from 'react-joyride';
import { Link } from 'react-router-dom';

import { AuthService } from '../../../modules/cloud';
import { CollectionService } from '../../../modules/collection';
import { useOnboardingContext } from '../../../modules/onboarding';
import { usePageHelper } from '../../blocksuite/block-suite-page-list/utils';
import { createTagFilter } from '../filter/utils';
import { createEmptyCollection } from '../use-collection-manager';
import {
  useEditCollection,
  useEditCollectionName,
} from '../view/use-edit-collection';
import * as styles from './page-list-header.css';
import { PageListNewPageButton } from './page-list-new-page-button';

export const PageListHeader = () => {
  const t = useI18n();
  const { workspaceService } = useServices({
    WorkspaceService,
  });

  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);

  const workspace = workspaceService.workspace;
  const { createPage } = usePageHelper(
    workspace.docCollection
  );

  const { isRunning, stepIndex, handleJoyrideCallback } =
    useOnboardingContext();

  const title = useMemo(() => {
    const firstName = account?.label?.split(' ')[0];
    return firstName
      ? `${firstName}'s Notes`
      : t['com.pique.all-pages.header']();
  }, [account?.label, t]);

  const handleCreateDoc = useCallback(
    (e?: MouseEvent) => {
      createPage(undefined, e && isNewTabTrigger(e) ? 'new-tab' : true);

      if (isRunning && stepIndex === 0) {
        const maxAttempts = 20;
        let attempts = 0;
        const pollForElement = () => {
          attempts++;
          const target = document.querySelector(
            '[data-testid="recording-button"]'
          );
          if (target) {
            handleJoyrideCallback({
              action: 'next',
              controlled: true,
              index: 0,
              lifecycle: 'complete',
              origin: null,
              size: 5,
              status: 'running',
              step: { content: '', target: 'body' } as Step,
              type: 'step:after',
            });
          } else if (attempts < maxAttempts) {
            setTimeout(pollForElement, 150);
          }
        };
        setTimeout(pollForElement, 300);
      }
    },
    [createPage, isRunning, stepIndex, handleJoyrideCallback]
  );

  return (
    <div className={styles.docListHeader}>
      <div className={styles.docListHeaderTitle}>{title}</div>
      <PageListNewPageButton
        size="small"
        testId="new-page-button-trigger"
        onCreateDoc={handleCreateDoc}
      >
        {t['New Page']()}
      </PageListNewPageButton>
    </div>
  );
};
export const CollectionPageListHeader = ({
  collection,
  workspaceId,
}: {
  collection: Collection;
  workspaceId: string;
}) => {
  const t = useI18n();
  const { jumpToCollections } = useNavigateHelper();
  const { collectionService } = useServices({
    CollectionService,
  });

  const handleJumpToCollections = useCallback(() => {
    jumpToCollections(workspaceId);
  }, [jumpToCollections, workspaceId]);

  const { node, open } = useEditCollection();

  const handleEdit = useAsyncCallback(async () => {
    const ret = await open({ ...collection });
    collectionService.updateCollection(collection.id, () => ret);
  }, [collection, collectionService, open]);

  return (
    <>
      {node}
      <div className={styles.docListHeader}>
        <div className={styles.docListHeaderTitle}>
          <ViewLayersIcon />
          <div style={{ cursor: 'pointer' }} onClick={handleJumpToCollections}>
            {t['com.pique.collections.header']()}
          </div>
          <span>/</span>
          <div className={styles.titleCollectionName}>{collection.name}</div>
          <SlidersVertical
            size={14}
            style={{ cursor: 'pointer', marginLeft: 12 }}
            onClick={handleEdit}
          />
        </div>
      </div>
    </>
  );
};

export const TagPageListHeader = ({
  tag,
  workspaceId,
}: {
  tag: Tag;
  workspaceId: string;
}) => {
  const tagColor = useLiveData(tag.color$);
  const tagTitle = useLiveData(tag.value$);

  const t = useI18n();
  const { jumpToTags, jumpToCollection } = useNavigateHelper();
  const collectionService = useService(CollectionService);
  const [openMenu, setOpenMenu] = useState(false);
  const { open, node } = useEditCollectionName({
    title: t['com.pique.editCollection.saveCollection'](),
    showTips: true,
  });

  const handleJumpToTags = useCallback(() => {
    jumpToTags(workspaceId);
  }, [jumpToTags, workspaceId]);

  const saveToCollection = useCallback(
    (collection: Collection) => {
      collectionService.addCollection({
        ...collection,
        filterList: [createTagFilter(tag.id)],
      });
      jumpToCollection(workspaceId, collection.id);
    },
    [collectionService, tag.id, jumpToCollection, workspaceId]
  );
  const handleClick = useCallback(() => {
    open('')
      .then(name => {
        return saveToCollection(createEmptyCollection(nanoid(), { name }));
      })
      .catch(err => {
        console.error(err);
      });
  }, [open, saveToCollection]);

  return (
    <>
      {node}
      <div className={styles.docListHeader}>
        <div className={styles.docListHeaderTitle}>
          <div
            style={{ cursor: 'pointer', lineHeight: '1.4em' }}
            onClick={handleJumpToTags}
          >
            {t['Tags']()} /
          </div>
          <Menu
            rootOptions={{
              open: openMenu,
              onOpenChange: setOpenMenu,
            }}
            contentOptions={{
              side: 'bottom',
              align: 'start',
              sideOffset: 18,
              avoidCollisions: false,
              className: styles.tagsMenu,
            }}
            items={<SwitchTag onClick={setOpenMenu} />}
          >
            <div className={styles.tagSticky}>
              <div
                className={styles.tagIndicator}
                style={{
                  backgroundColor: tagColor,
                }}
              />
              <div className={styles.tagLabel}>{tagTitle}</div>
              <ArrowDownSmallIcon className={styles.arrowDownSmallIcon} />
            </div>
          </Menu>
        </div>
        <Button onClick={handleClick}>
          {t['com.pique.editCollection.saveCollection']()}
        </Button>
      </div>
    </>
  );
};

interface SwitchTagProps {
  onClick: (open: boolean) => void;
}

export const SwitchTag = ({ onClick }: SwitchTagProps) => {
  const t = useI18n();
  const [inputValue, setInputValue] = useState('');
  const tagList = useService(TagService).tagList;
  const filteredTags = useLiveData(
    inputValue ? tagList.filterTagsByName$(inputValue) : tagList.tags$
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const handleClick = useCallback(() => {
    setInputValue('');
    onClick(false);
  }, [onClick]);

  return (
    <div className={styles.tagsEditorRoot}>
      <div className={styles.tagsEditorSelectedTags}>
        <SearchIcon className={styles.searchIcon} />
        <input
          value={inputValue}
          onChange={onInputChange}
          autoFocus
          className={styles.searchInput}
          placeholder={t['com.pique.search-tags.placeholder']()}
        />
      </div>
      <Divider />
      <div className={styles.tagsEditorTagsSelector}>
        <Scrollable.Root>
          <Scrollable.Viewport
            className={styles.tagSelectorTagsScrollContainer}
          >
            {filteredTags.map((tag: Tag) => {
              return <TagLink key={tag.id} tag={tag} onClick={handleClick} />;
            })}
            {filteredTags.length === 0 ? (
              <div className={clsx(styles.tagSelectorItem, 'disable')}>
                {t['Find 0 result']()}
              </div>
            ) : null}
          </Scrollable.Viewport>
          <Scrollable.Scrollbar style={{ transform: 'translateX(6px)' }} />
        </Scrollable.Root>
      </div>
    </div>
  );
};

const TagLink = ({ tag, onClick }: { tag: Tag; onClick: () => void }) => {
  const tagColor = useLiveData(tag.color$);
  const tagTitle = useLiveData(tag.value$);
  return (
    <Link
      key={tag.id}
      className={styles.tagSelectorItem}
      data-tag-id={tag.id}
      data-tag-value={tagTitle}
      to={`/tag/${tag.id}`}
      onClick={onClick}
    >
      <div className={styles.tagIcon} style={{ background: tagColor }} />
      <div className={styles.tagSelectorItemText}>{tagTitle}</div>
    </Link>
  );
};
